import React, { Fragment, useContext, useEffect, useRef, useState } from 'react'
import { AuthenticationContext } from '../../Auth/authentication.context'
import { Toast } from 'primereact/toast'
import { ToastPopUp } from '../shared/utils'
import { Form, FormFeedback, Label, Input, InputGroup, InputGroupText } from 'reactstrap'

// Formik validation
import * as Yup from 'yup'
import { useFormik } from 'formik'

import { Link, useSearchParams, Navigate } from 'react-router-dom'

import LoggedOutFooter from '../Layout/LoggedOutFooter'
import AuthFormsLayout from '../Layout/AuthFormsLayout'

const ChangePassword = (props) => {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const { user, error, onChangePassword, toastStatus, setUser, setToastStatus } = useContext(
    AuthenticationContext
  )

  const toast = useRef(null)
  useEffect(() => {
    if (toastStatus.toastStatus !== undefined) {
      ToastPopUp(toast, toastStatus?.toastStatus, toastStatus?.msg)
    }
  }, [toastStatus])
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  
  // Debug logging
  console.log('ChangePassword - user:', user)
  console.log('ChangePassword - token:', token)
  
  // If we have a token, we should show the password reset form regardless of user state
  // The token is the primary indicator for password reset flow
  useEffect(() => {
    if (token && user) {
      // Clear any existing session when accessing password reset
      console.log('Clearing user session for password reset')
      localStorage.removeItem('accessToken')
      localStorage.removeItem('user')
      localStorage.removeItem('isAdmin')
      setUser(null)
    }
  }, [token, user, setUser])

  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .matches(
          passwordRegex,
          'Password must contain: minimum 8 characters, at least one uppercase letter, at least one lowercase letter, at least one number, and at least one special character (@ $ ! % * # ? &)'
        )
        .required('Please Enter Your Password'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Required'),
    }),
    onSubmit: (values) => {
      if (!token) {
        setToastStatus({
          toastStatus: 'error',
          msg: 'No reset token provided. Please request a new password reset.',
        })
        return
      }
      onChangePassword(token, values.password, values.confirmPassword)
    },
  })

  const passwordValue = validation.values.password || ''
  const passwordRules = {
    minLength: passwordValue.length >= 8,
    hasLowercase: /[a-z]/.test(passwordValue),
    hasUppercase: /[A-Z]/.test(passwordValue),
    hasNumber: /\d/.test(passwordValue),
    hasSpecial: /[@$!%*#?&]/.test(passwordValue),
  }

  const renderRule = (met, label) => (
    <div
      className={`d-flex align-items-center ${met ? 'text-success' : 'text-danger'}`}
    >
      <i
        className={`mdi ${met ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'} me-1`}
      />
      <span>{label}</span>
    </div>
  )

  // handleValidSubmit
  // const handleValidSubmit = (event, values) => {
  //   props.registerUser(values);
  // };

  document.title = 'Change Password | Origins Direct'
  return (
    <React.Fragment>
      <div className='card flex justify-content-center'>
        <Toast ref={toast} />
      </div>
      {!token ? (
        <AuthFormsLayout title='Invalid Reset Link' subtitle='No reset token provided'>
          <div className='alert alert-danger'>
            <p>Invalid or missing reset token. Please request a new password reset.</p>
            <Link to='/forget-password' className='btn btn-primary'>
              Request New Reset Link
            </Link>
          </div>
        </AuthFormsLayout>
      ) : (
        <>
          <AuthFormsLayout title='Change Password' subtitle='Enter your new password'>
            <Form
              className='mt-4'
              onSubmit={(e) => {
                e.preventDefault()
                validation.handleSubmit()
                return false
              }}
              action='#'
            >
              {/* {error && <p style={{ color: 'red' }}>{error}</p>} */}
              <div className='mb-3'>
                <Label className='form-label' htmlFor='userpassword'>
                  Password
                </Label>
                <InputGroup>
                  <Input
                    name='password'
                    value={validation.values.password || ''}
                    type={showPassword ? 'text' : 'password'}
                    id='userpassword'
                    className='form-control'
                    placeholder='Enter Password'
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.password && validation.errors.password
                        ? true
                        : false
                    }
                  />
                  <InputGroupText
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ cursor: 'pointer' }}
                  >
                    <i
                      className={`mdi ${
                        showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
                      }`}
                    />
                  </InputGroupText>
                </InputGroup>
                {validation.touched.password && validation.errors.password ? (
                  <FormFeedback type='invalid'>
                    {validation.errors.password}
                  </FormFeedback>
                ) : null}
                {/* Inline password requirements helper */}
                {(validation.touched.password || passwordValue.length > 0) && (
                  <div className='mt-2 small'>
                    <div className='text-muted mb-1'>
                      Password must include:
                    </div>
                    {renderRule(passwordRules.minLength, 'At least 8 characters')}
                    {renderRule(
                      passwordRules.hasUppercase,
                      'At least 1 uppercase letter (A-Z)'
                    )}
                    {renderRule(
                      passwordRules.hasLowercase,
                      'At least 1 lowercase letter (a-z)'
                    )}
                    {renderRule(
                      passwordRules.hasNumber,
                      'At least 1 number (0-9)'
                    )}
                    {renderRule(
                      passwordRules.hasSpecial,
                      'At least 1 special character (@ $ ! % * # ? &)'
                    )}
                  </div>
                )}
              </div>

              <div className='mb-3'>
                <Label className='form-label' htmlFor='confirmPassword'>
                  Repeat Password
                </Label>
                <InputGroup>
                  <Input
                    name='confirmPassword'
                    value={validation.values.confirmPassword || ''}
                    type={showConfirmPassword ? 'text' : 'password'}
                    id='confirmPassword'
                    className='form-control'
                    placeholder='Repeat Password'
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.confirmPassword &&
                      validation.errors.confirmPassword
                        ? true
                        : false
                    }
                  />
                  <InputGroupText
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{ cursor: 'pointer' }}
                  >
                    <i
                      className={`mdi ${
                        showConfirmPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
                      }`}
                    />
                  </InputGroupText>
                </InputGroup>
                {validation.touched.confirmPassword &&
                validation.errors.confirmPassword ? (
                  <FormFeedback type='invalid'>
                    {validation.errors.confirmPassword}
                  </FormFeedback>
                ) : null}
              </div>

              <div className='mb-3 row'>
                <div className='col-12 text-end'>
                  <button
                    className='btn btn-primary w-md waves-effect waves-light'
                    type='submit'
                  >
                    Create
                  </button>
                </div>
              </div>
            </Form>
          </AuthFormsLayout>

          <div className='mt-2 text-center'>
            <p>
              Already have an account ?
              <Link to='/login' className='fw-medium text-primary'>
                &nbsp;Login
              </Link>
            </p>
          </div>
          {/* <LoggedOutFooter /> */}
        </>
      )}
    </React.Fragment>
  )
}

export default ChangePassword
